import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BellRing, CalendarClock, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useMutation } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { fetchExtensions, fetchQueues } from '@/services/api';
import type { Extension, Queue } from '@/types';
import { FAILOVER_OUTCOME_OPTIONS } from '@/lib/reports/failoverReport';
import { CALL_COLUMNS_KEY, EXTENSION_COLUMNS_KEY } from '@/lib/reports/callCostReport';
import {
  CHECK_INTERVALS,
  DATE_RANGES,
  DATE_RANGE_LABELS,
  MAX_INLINE_ROW_LIMIT,
  MAX_RECIPIENTS,
  REPORT_KINDS,
  REPORT_KIND_LABELS,
  WEEKDAYS,
  defaultDateRange,
  describeTiming,
  isAlertKind,
  nextRunAfter,
  parseRecipients,
  scheduleDefaults,
  type DateRangeKey,
  type Frequency,
  type ReportFormat,
  type ReportKind,
  type ScheduleInput,
} from '@/lib/reports/schedule';
import { compareExtensions } from './reportDates';

/** The form for a scheduled report or alert: what, filters, when, and who gets it. */

export type ScheduleDraft = Omit<ScheduleInput, 'filters'> & { filters: Record<string, any> };

interface ScheduleEditorProps {
  isOpen: boolean;
  onClose: () => void;
  /** What the form starts with: a preset, a report page's current filters, or a saved schedule. */
  initial: ScheduleDraft;
  /** Editing a saved schedule. */
  id?: string;
  /** Let the person pick another report (not when it comes from a report page). */
  allowKindChange?: boolean;
  onSaved?: () => void;
}

const fieldClass =
  'px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-primary-500';
const inputClass =
  'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-primary-500';

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</span>}
    </label>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h4>
      {children}
    </section>
  );
}

function Check({ checked, onChange, children }: { checked: boolean; onChange: (value: boolean) => void; children: ReactNode }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      {children}
    </label>
  );
}

function readColumns(key: string) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

const rateValue = (value: unknown) => (typeof value === 'number' ? value.toFixed(2) : String(value ?? ''));

export function ScheduleEditor({ isOpen, onClose, initial, id, allowKindChange = false, onSaved }: ScheduleEditorProps) {
  const save = useMutation(backendApi.scheduledReports.save);
  const [draft, setDraft] = useState<ScheduleDraft>(initial);
  const [recipientsText, setRecipientsText] = useState(initial.recipients.join(', '));
  const [saving, setSaving] = useState(false);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [queues, setQueues] = useState<Queue[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(initial);
    setRecipientsText(initial.recipients.join(', '));
    // Opening the form again starts from what it was opened with
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const kind = draft.reportKind;
  const alert = isAlertKind(kind);
  const needsExtensions = kind === 'summary' || kind === 'detail' || kind === 'costs';
  const needsQueues = kind === 'queueMissed' || kind === 'failover';

  useEffect(() => {
    if (!isOpen || !needsExtensions || extensions.length) return;
    fetchExtensions()
      .then((list) => setExtensions([...list].sort(compareExtensions)))
      .catch(() => toast.error('Could not load extensions'));
  }, [isOpen, needsExtensions, extensions.length]);

  useEffect(() => {
    if (!isOpen || !needsQueues || queues.length) return;
    fetchQueues()
      .then(setQueues)
      .catch(() => toast.error('Could not load queues'));
  }, [isOpen, needsQueues, queues.length]);

  const set = <K extends keyof ScheduleDraft>(key: K, value: ScheduleDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const setFilter = (key: string, value: unknown) => setDraft((d) => ({ ...d, filters: { ...d.filters, [key]: value } }));

  const changeKind = (next: ReportKind) => {
    const defaults = scheduleDefaults(next);
    setDraft((d) => ({
      ...defaults,
      filters: defaults.filters as Record<string, any>,
      recipients: d.recipients,
      // Keep a name the person typed
      name: d.name && d.name !== REPORT_KIND_LABELS[d.reportKind] ? d.name : defaults.name,
    }));
  };

  const changeFrequency = (frequency: Frequency) =>
    setDraft((d) => ({
      ...d,
      frequency,
      weekday: frequency === 'weekly' ? (d.weekday ?? 1) : undefined,
      dayOfMonth: frequency === 'monthly' ? (d.dayOfMonth ?? 1) : undefined,
      dateRange: d.dateRange === defaultDateRange(d.frequency) ? defaultDateRange(frequency) : d.dateRange,
    }));

  const recipients = useMemo(() => parseRecipients(recipientsText), [recipientsText]);
  const nextRun = useMemo(() => {
    try {
      return draft.enabled ? new Date(nextRunAfter(draft, new Date())) : null;
    } catch {
      return null;
    }
  }, [draft]);

  const submit = async () => {
    if (recipients.invalid.length) {
      toast.error(`Not an email address: ${recipients.invalid.slice(0, 3).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      await save({ id, schedule: { ...draft, recipients: recipients.valid } });
      toast.success(id ? 'Schedule saved' : alert ? 'Alert set up' : 'Report scheduled');
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const f = draft.filters;
  const queueChecks = (
    <div className="space-y-2">
      <Check checked={(f.queueIds ?? []).length === 0} onChange={(all) => setFilter('queueIds', all ? [] : queues.map((q) => q.id))}>
        All queues, including ones added later
      </Check>
      {(f.queueIds ?? []).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-2">
          {queues.map((queue) => (
            <Check
              key={queue.id}
              checked={f.queueIds.includes(queue.id)}
              onChange={(on) =>
                setFilter('queueIds', on ? [...f.queueIds, queue.id] : f.queueIds.filter((x: string) => x !== queue.id))
              }
            >
              {queue.name}
              {queue.number && queue.number !== queue.name ? ` (${queue.number})` : ''}
            </Check>
          ))}
        </div>
      )}
    </div>
  );

  const extensionSelect = (allowAll: boolean) => (
    <select value={f.extension ?? ''} onChange={(event) => setFilter('extension', event.target.value)} className={inputClass}>
      {allowAll ? <option value="all">All extensions</option> : <option value="">Choose an extension…</option>}
      {extensions.map((ext) => (
        <option key={ext.id} value={ext.number}>
          {ext.number} - {ext.display_name || ext.username || ''}
        </option>
      ))}
    </select>
  );

  const rateInputs = (
    <div className="grid grid-cols-2 gap-3">
      {(
        [
          ['mobileRate', 'Mobile rate (R/min)'],
          ['landlineRate', 'Landline rate (R/min)'],
        ] as const
      ).map(([key, label]) => (
        <Field key={key} label={label}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={rateValue(f[key])}
            onChange={(event) => setFilter(key, Number(event.target.value))}
            className={inputClass}
          />
        </Field>
      ))}
    </div>
  );

  const filterFields = (() => {
    switch (kind) {
      case 'activity':
        return (
          <>
            {rateInputs}
            <Check checked={f.includeCallLists !== false} onChange={(on) => setFilter('includeCallLists', on)}>
              List each extension’s calls made, one by one
            </Check>
          </>
        );
      case 'missedAlert':
        return (
          <Field label="Report a missed call when nobody has returned it within" hint="Minutes after the call was missed">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="600"
                value={f.thresholdMinutes ?? 15}
                onChange={(event) => setFilter('thresholdMinutes', Number(event.target.value))}
                className={`${fieldClass} w-24`}
              />
              <span className="text-sm text-gray-600 dark:text-gray-300">minutes</span>
            </div>
          </Field>
        );
      case 'summary':
      case 'detail':
        return <Field label="Extension">{extensionSelect(false)}</Field>;
      case 'costs': {
        const columns = f.callColumns && Object.keys(f.callColumns.visible ?? {}).length > 0;
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Extension">{extensionSelect(true)}</Field>
              <Field label="Calls listed">
                <select value={f.callFilter ?? 'outbound'} onChange={(event) => setFilter('callFilter', event.target.value)} className={inputClass}>
                  <option value="outbound">Outbound calls</option>
                  <option value="all">All calls</option>
                </select>
              </Field>
            </div>
            {rateInputs}
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span>{columns ? 'Columns and sort as chosen on the Call Costs page.' : 'Standard columns and sort.'}</span>
              <button
                type="button"
                onClick={() => {
                  setFilter('callColumns', readColumns(CALL_COLUMNS_KEY));
                  setDraft((d) => ({ ...d, filters: { ...d.filters, extensionColumns: readColumns(EXTENSION_COLUMNS_KEY) } }));
                  toast.success('Using your current Call Costs columns');
                }}
                className="text-primary-700 dark:text-primary-400 hover:underline"
              >
                Use my current Call Costs columns
              </button>
            </div>
          </>
        );
      }
      case 'missed':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Callback status">
              <select
                value={f.callbackStatus ?? ''}
                onChange={(event) => setFilter('callbackStatus', event.target.value ? Number(event.target.value) : undefined)}
                className={inputClass}
              >
                <option value="2">Unreturned only</option>
                <option value="1">Returned only</option>
                <option value="">Both</option>
              </select>
            </Field>
            <Field label="Destination">
              <select value={f.callToType ?? ''} onChange={(event) => setFilter('callToType', event.target.value || undefined)} className={inputClass}>
                <option value="">Any</option>
                <option value="extension">Extension</option>
                <option value="queue">Queue</option>
                <option value="ring_group">Ring group</option>
              </select>
            </Field>
            <Field label="Missed type">
              <select value={f.missCallType ?? ''} onChange={(event) => setFilter('missCallType', event.target.value || undefined)} className={inputClass}>
                <option value="">Any</option>
                <option value="no_answer">No answer</option>
                <option value="busy">Busy</option>
                <option value="abandoned">Abandoned</option>
              </select>
            </Field>
            <Field label="Ignore abandoned within (seconds)">
              <input type="number" min="0" value={f.abandonTime ?? ''} onChange={(event) => setFilter('abandonTime', event.target.value === '' ? undefined : Number(event.target.value))} className={inputClass} />
            </Field>
            <Field label="Caller contains">
              <input value={f.callFrom ?? ''} onChange={(event) => setFilter('callFrom', event.target.value)} className={inputClass} />
            </Field>
            <Field label="Called contains">
              <input value={f.callTo ?? ''} onChange={(event) => setFilter('callTo', event.target.value)} className={inputClass} />
            </Field>
          </div>
        );
      case 'queueMissed':
        return (
          <>
            {queueChecks}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Calls">
                <select value={f.statusFilter ?? 'missed'} onChange={(event) => setFilter('statusFilter', event.target.value)} className={inputClass}>
                  <option value="missed">Missed and abandoned only</option>
                  <option value="all">All queue calls</option>
                </select>
              </Field>
              <Field label="Caller contains">
                <input value={f.callFrom ?? ''} onChange={(event) => setFilter('callFrom', event.target.value)} className={inputClass} />
              </Field>
            </div>
          </>
        );
      case 'failover':
        return (
          <>
            {queueChecks}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Failover outcome">
                <select value={f.outcome ?? 'all'} onChange={(event) => setFilter('outcome', event.target.value)} className={inputClass}>
                  {FAILOVER_OUTCOME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Search">
                <input value={f.search ?? ''} onChange={(event) => setFilter('search', event.target.value)} className={inputClass} />
              </Field>
            </div>
          </>
        );
      default:
        return null;
    }
  })();

  const timeInput = (key: 'time' | 'endTime', label: string) => (
    <Field label={label}>
      <input type="time" value={draft[key] ?? ''} onChange={(event) => set(key, event.target.value)} className={inputClass} />
    </Field>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={id ? 'Edit schedule' : alert ? 'Set up an alert' : 'Schedule a report'} size="lg">
      <div className="space-y-6">
        <div className="flex items-start gap-3 rounded-xl bg-primary-50 dark:bg-primary-900/20 p-3 text-sm text-primary-900 dark:text-primary-100">
          {alert ? <BellRing className="w-5 h-5 shrink-0 mt-0.5" /> : <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" />}
          <span>
            {alert
              ? 'Checks the calls on a schedule and emails only when it finds missed calls nobody has returned. Nothing is sent when there are none, and each call is reported once.'
              : 'The report is built from the phone system at the send time and emailed with the filters below.'}
          </span>
        </div>

        <Section title="What">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name">
              <input value={draft.name} maxLength={80} onChange={(event) => set('name', event.target.value)} className={inputClass} />
            </Field>
            {!alert && (
              <Field label="Report">
                <select
                  value={kind}
                  disabled={!allowKindChange}
                  onChange={(event) => changeKind(event.target.value as ReportKind)}
                  className={`${inputClass} disabled:opacity-70`}
                >
                  {REPORT_KINDS.filter((k) => !isAlertKind(k)).map((k) => (
                    <option key={k} value={k}>
                      {REPORT_KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          {filterFields}
        </Section>

        <Section title="When">
          {alert ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Check every">
                <select
                  value={draft.intervalMinutes ?? 60}
                  onChange={(event) => set('intervalMinutes', Number(event.target.value))}
                  className={inputClass}
                >
                  {CHECK_INTERVALS.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {minutes < 60 ? `${minutes} minutes` : minutes === 60 ? 'hour' : `${minutes / 60} hours`}
                    </option>
                  ))}
                </select>
              </Field>
              {timeInput('time', 'From')}
              {timeInput('endTime', 'Until')}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Send">
                <select value={draft.frequency} onChange={(event) => changeFrequency(event.target.value as Frequency)} className={inputClass}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
              {draft.frequency === 'weekly' && (
                <Field label="On">
                  <select value={draft.weekday ?? 1} onChange={(event) => set('weekday', Number(event.target.value))} className={inputClass}>
                    {WEEKDAYS.map((day) => (
                      <option key={day.day} value={day.day}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {draft.frequency === 'monthly' && (
                <Field label="On day">
                  <select value={draft.dayOfMonth ?? 1} onChange={(event) => set('dayOfMonth', Number(event.target.value))} className={inputClass}>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {timeInput('time', 'At')}
              <Field label="Covering">
                <select value={draft.dateRange} onChange={(event) => set('dateRange', event.target.value as DateRangeKey)} className={inputClass}>
                  {DATE_RANGES.map((range) => (
                    <option key={range} value={range}>
                      {DATE_RANGE_LABELS[range]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
          {(alert || draft.frequency === 'daily') && (
            <Check checked={draft.weekdaysOnly === true} onChange={(on) => set('weekdaysOnly', on)}>
              Weekdays only (Monday to Friday)
            </Check>
          )}
          <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <Info className="w-3.5 h-3.5" />
            {describeTiming(draft)}, South African time.
            {nextRun && ` Next ${alert ? 'check' : 'send'}: ${nextRun.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.`}
          </p>
        </Section>

        <Section title="Who gets it">
          <Field
            label="Email addresses"
            hint={`Separate with commas or new lines, up to ${MAX_RECIPIENTS}. They don't need a Yeastarify login.`}
          >
            <textarea
              rows={2}
              value={recipientsText}
              onChange={(event) => setRecipientsText(event.target.value)}
              placeholder="manager@company.co.za"
              className={inputClass}
            />
          </Field>
          {recipients.invalid.length > 0 && (
            <p className="text-sm text-red-600 dark:text-red-400">Not an email address: {recipients.invalid.join(', ')}</p>
          )}
          {!alert && (
            <div className="space-y-3 rounded-xl bg-panel dark:bg-gray-900/40 p-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                The headline numbers and summary tables are always in the email itself.
              </p>
              <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                Call lists go in the email when they have up to
                <input
                  type="number"
                  min="0"
                  max={MAX_INLINE_ROW_LIMIT}
                  value={draft.inlineRowLimit}
                  onChange={(event) => set('inlineRowLimit', Math.max(0, Number(event.target.value) || 0))}
                  className={`${fieldClass} w-20`}
                />
                rows. Longer lists are attached.
              </label>
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-sm text-gray-700 dark:text-gray-300">Attach as</span>
                {(['pdf', 'csv'] as ReportFormat[]).map((format) => (
                  <Check
                    key={format}
                    checked={draft.formats.includes(format)}
                    onChange={(on) => set('formats', on ? [...draft.formats, format] : draft.formats.filter((x) => x !== format))}
                  >
                    {format.toUpperCase()}
                  </Check>
                ))}
              </div>
              <Check checked={draft.attachAlways} onChange={(on) => set('attachAlways', on)}>
                Always attach the files, even when everything fits in the email
              </Check>
            </div>
          )}
          <Check checked={draft.enabled} onChange={(on) => set('enabled', on)}>
            Active
          </Check>
        </Section>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} isLoading={saving}>
            {id ? 'Save' : alert ? 'Set up alert' : 'Schedule'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
